import React, { useEffect, useState } from 'react';
import useApiStable from '../../utils/useApiStable';
import '../../styles/TaskList.css';

function diffMinutes(start, end) {
  if (!start || !end) return 120;
  return Math.round((new Date(end) - new Date(start)) / 60000);
}

function ClubEventForm({ event, onClose }) {
  const api = useApiStable();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startTime, setStartTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(120);
  const [registrationDeadline, setRegistrationDeadline] = useState('');
  const [participantIds, setParticipantIds] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [organizerId, setOrganizerId] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingDetails, setLoadingDetails] = useState(!!event?.id);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!event?.id) {
      return;
    }
    api.get(`/appointments/${event.id}`)
      .then(data => {
        setTitle(data.title || '');
        setDescription(data.description || '');
        setLocation(data.location || '');
        setStartTime(data.startTime ? data.startTime.substring(0, 16) : '');
        setDurationMinutes(diffMinutes(data.startTime, data.endTime));
        setRegistrationDeadline(data.registrationDeadline || '');
        setParticipantIds((data.participants || []).map(p => p.id));
        setGroupIds((data.groups || []).map(g => g.id));
        setOrganizerId(data.organizer ? data.organizer.id : '');
        setNotes(data.notes || '');
      })
      .catch(err => setError(err.message || 'Fehler beim Laden des Termins'))
      .finally(() => setLoadingDetails(false));
  }, [event?.id]); // eslint-disable-line

  const buildPayload = (status) => ({
    title,
    description,
    startTime,
    durationMinutes: Number(durationMinutes),
    location,
    type: 'CLUB_EVENT',
    registrationDeadline: registrationDeadline || null,
    groupIds,
    participantIds,
    organizerId: organizerId ? Number(organizerId) : null,
    status,
    notes,
  });

  const handleResetDeadline = () => {
    if (!window.confirm('Anmeldefrist zurücksetzen? Beim Speichern werden dadurch alle bisherigen Zu-/Absagen zu diesem Termin gelöscht, und es ist keine Anmeldung über die App mehr möglich, bis eine neue Frist gesetzt wird.')) {
      return;
    }
    setRegistrationDeadline('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = buildPayload('SCHEDULED');
    const request = event?.id
      ? api.put(`/appointments/${event.id}`, payload)
      : api.post('/appointments', payload);
    request
      .then(() => onClose(true))
      .catch(err => setError(err.message || 'Fehler beim Speichern des Termins'))
      .finally(() => setSaving(false));
  };

  const handleCancelEvent = () => {
    if (!event?.id) return;
    if (!window.confirm('Diesen Vereinstermin wirklich absagen?')) return;
    setError('');
    setCancelling(true);
    api.put(`/appointments/${event.id}`, buildPayload('CANCELLED'))
      .then(() => onClose(true))
      .catch(err => setError(err.message || 'Fehler beim Absagen des Termins'))
      .finally(() => setCancelling(false));
  };

  if (loadingDetails) {
    return <div className="taskform-card"><p>Lade Termin...</p></div>;
  }

  return (
    <div className="taskform-card">
      <h3 style={{ marginBottom: '18px' }}>{event ? 'Vereinstermin bearbeiten' : 'Neuen Vereinstermin anlegen'}</h3>
      {error && <div className="taskform-error">{error}</div>}
      <form onSubmit={handleSubmit} className={saving ? 'taskform-loading' : ''}>
        <div className="taskform-group">
          <label htmlFor="ce-title">Titel *</label>
          <input id="ce-title" value={title} onChange={e => setTitle(e.target.value)} required disabled={saving} />
        </div>
        <div className="taskform-group">
          <label htmlFor="ce-desc">Beschreibung</label>
          <textarea id="ce-desc" value={description} onChange={e => setDescription(e.target.value)} disabled={saving} maxLength={1000} />
        </div>
        <div className="taskform-inline-row">
          <div className="taskform-group">
            <label htmlFor="ce-start">Start *</label>
            <input id="ce-start" type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} required disabled={saving} />
          </div>
          <div className="taskform-group">
            <label htmlFor="ce-duration">Dauer (Minuten) *</label>
            <input id="ce-duration" type="number" min="15" step="15" value={durationMinutes}
                   onChange={e => setDurationMinutes(e.target.value)} required disabled={saving} />
          </div>
        </div>
        <div className="taskform-inline-row">
          <div className="taskform-group">
            <label htmlFor="ce-location">Ort</label>
            <input id="ce-location" value={location} onChange={e => setLocation(e.target.value)} disabled={saving} />
          </div>
          <div className="taskform-group">
            <label htmlFor="ce-deadline">Anmeldefrist</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input id="ce-deadline" type="date" value={registrationDeadline}
                     onChange={e => setRegistrationDeadline(e.target.value)} disabled={saving} />
              <button type="button" title="Anmeldefrist zurücksetzen" onClick={handleResetDeadline}
                      disabled={saving || !registrationDeadline}
                      style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px' }}>
                ✕
              </button>
            </div>
            <small style={{ color: '#666', display: 'block', marginTop: 4 }}>
              Ohne Anmeldefrist ist für Mitglieder keine Anmeldung über die App möglich.
            </small>
          </div>
        </div>
        <div className="taskform-actions" style={{ marginTop: '6px' }}>
          {event && event.id && (
            <button type="button" className="taskform-delete-btn" onClick={handleCancelEvent}
                    disabled={saving || cancelling} style={{ marginRight: 'auto', background: '#dc3545', color: '#fff' }}>
              {cancelling ? 'Sage ab...' : 'Termin absagen'}
            </button>
          )}
          <button type="button" className="taskform-cancel-btn" onClick={() => onClose(false)} disabled={saving || cancelling}>Abbrechen</button>
          <button type="submit" className="taskform-save-btn" disabled={saving || cancelling || !title || !startTime}>
            Speichern
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClubEventForm;
